import { useCallback } from "react";

import { GqlPlannerV2TransportationMethod } from "~/__generated__/graphql/types.ts";

import usePlannerV2CancelTravelGroup from "../screens/PlannerV2Screen/PlannerV2VisitDetailScreen/hooks/usePlannerV2CancelTravelGroup";
import usePlannerV2CreateShift from "../screens/PlannerV2Screen/PlannerV2VisitDetailScreen/hooks/usePlannerV2CreateShift";
import usePlannerV2TravelGroupAddFieldworker from "../screens/PlannerV2Screen/PlannerV2VisitDetailScreen/hooks/usePlannerV2TravelGroupAddFieldworker";
import usePlannerV2CancelShiftInTravelGroup from "../screens/PlannerV2Screen/PlannerV2VisitDetailScreen/hooks/usePlannerV2TravelGroupCancelShift";

export function usePlannerV2VisitActions({
    date,
    visitId,
    selectedTimeslotId,
    allowIfUnavailable = false,
}: {
    date: string;
    visitId: string;
    selectedTimeslotId?: string;
    allowIfUnavailable?: boolean;
}) {
    const {
        addFieldworkerToTravelGroup: addFieldworkerToTravelGroupMutation,
        loading: isLoadingAddingToTravelGroup,
        error: addingToGroupError,
    } = usePlannerV2TravelGroupAddFieldworker();
    const { cancelShiftInTravelGroup, isCancelingShiftId, error: cancelingShiftError } = usePlannerV2CancelShiftInTravelGroup();
    const { cancelTravelGroup, isCancelingGroupId, error: cancellingGroupError } = usePlannerV2CancelTravelGroup();
    const {
        createShift: createShiftMutation,
        error: errorCreatingShift,
        loading: isLoadingCreatingShift,
    } = usePlannerV2CreateShift();

    const addFieldworkerToShift = useCallback(
        async (fieldworkerId: string, transportationMethod: GqlPlannerV2TransportationMethod) => {
            if (!selectedTimeslotId || !visitId) {
                throw new Error("Cannot add fieldworker: missing timeslot or visit ID");
            }

            return await createShiftMutation({
                allowIfUnavailable: allowIfUnavailable,
                fieldworkerId: fieldworkerId,
                onDate: date,
                plannerVisitId: visitId,
                timeslotId: selectedTimeslotId,
                transportationMethod: transportationMethod,
            });
        },
        [selectedTimeslotId, visitId, date, createShiftMutation, allowIfUnavailable],
    );

    const addFieldworkerToTravelGroup = useCallback(
        async (input: { plannerTravelGroupId: string; fieldworkerId: string }) => {
            return await addFieldworkerToTravelGroupMutation({
                ...input,
                allowIfUnavailable,
            });
        },
        [addFieldworkerToTravelGroupMutation, allowIfUnavailable],
    );

    return {
        addFieldworkerToShift,
        addFieldworkerToTravelGroup,
        cancelTravelGroup,
        cancelShiftInTravelGroup,

        isAddingAnyFieldworker: isLoadingCreatingShift || isLoadingAddingToTravelGroup,
        isCancelingShiftId,
        isCancelingGroupId,

        fieldworkerCancelError: cancellingGroupError || cancelingShiftError,
        fieldworkerAddingError: addingToGroupError || errorCreatingShift,
    };
}
